import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Box, Collapse, Divider, Grid, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-7";
import React, { SetStateAction, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import SortedDataGrid7 from "../../../../../design/sorted-datagrid-7";
import useTableHeight from "../../../../../utils/use-table-height";
import AppliedFilters from "../../../../filters/applied-filters";
import { Filter } from "../../../../filters/filter-accordion/types";
import { AppliedFilter, FilterContext } from "../../../../filters/filter-context";
import useMonthlyActivityColumn from "../../../controllers/use-monthly-activity-column";
import { useWorkerActivityData } from "../../../controllers/use-worker-activity-data";
import {
	ColumnSelector,
	WorkerActivityData,
	WorkerActivityFilters,
} from "../../../store/worker-activity-slice-types";
import KpiSummaries from "../kpi-summaries";

const NoReportsAvailable = () => {
	const { t } = useTranslation();
	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Box display='flex' flexDirection='column' alignItems='center'>
				<CalendarTodayIcon fontSize='large' />
				<Typography>{t("worker-activity_no-workers-found")}</Typography>
			</Box>
		</div>
	);
};
const MonthlyActivityTable = ({
	data,
	search,
	columnSelector,
	isFilterOpen = false,
	selectedKPI,
	setSelectedKPI,
	KPICount,
	defaultAppliedFilters = [],
}: {
	data?: WorkerActivityData[];
	search?: string;
	columnSelector?: ColumnSelector[];
	isFilterOpen?: boolean;
	selectedKPI?: string;
	setSelectedKPI?: (selectedKPI: string) => void;
	KPICount: any;
	defaultAppliedFilters?: AppliedFilter[];
}) => {
	const { boxRef, height } = useTableHeight();
	const { monthlyActivityReportStatus, currentUserId } = useWorkerActivityData();
	const columns = useMonthlyActivityColumn(search || "");
	const filterContext = useContext(FilterContext);
  if (!filterContext) {
    throw new Error("FilterContext must be used within a FilterProvider");
  }
  const { appliedFilters, filters, setFilters } = filterContext;
	const [initialized, setInitialized] = useState(false);
	
	// Handle default filters and existing filters
	useEffect(() => {
		if (filterContext && defaultAppliedFilters.length > 0 && !initialized) {
			const { setAppliedFilters } = filterContext;
			
			// Combine existing filters with default filters
			if (appliedFilters.length === 0) {
				// If no filters are applied yet, just use the defaults
				setAppliedFilters(defaultAppliedFilters);
			} else {
				// For each default filter, check if it already exists
				defaultAppliedFilters.forEach(defaultFilter => {
					const existingFilterIndex = appliedFilters.findIndex(
						filter => filter.type === defaultFilter.type
					);
					
					if (existingFilterIndex === -1) {
						// This filter type doesn't exist in current filters, so add it
						setAppliedFilters(prev => [...prev, defaultFilter]);
					} else {
						// This filter type exists, check if the specific values are already applied
						const existingFilter = appliedFilters[existingFilterIndex];
						const newData = [...existingFilter.data];
						let needsUpdate = false;
						
						defaultFilter.data.forEach(defaultItem => {
							// Check if this specific item exists in the current filter
							const itemExists = existingFilter.data.some(
								item => item.id === defaultItem.id
							);
							
							if (!itemExists) {
								newData.push(defaultItem);
								needsUpdate = true;
							}
						});
						
						if (needsUpdate) {
							// Update the filter with the new combined data
							setAppliedFilters(prev => {
								const newFilters = [...prev];
								newFilters[existingFilterIndex] = {
									...existingFilter,
									data: newData
								};
								return newFilters;
							});
						}
					}
				});
			}
			
			setInitialized(true);
		}
	}, [filterContext, appliedFilters, defaultAppliedFilters, initialized]);

	const filteredColumns = columns.filter((column) =>
		columnSelector?.some((c) => column.field === c.field && c.checked)
	);

	// remove duplicate rows by id
	const uniqueData = data?.filter(
		(item, index, self) => index === self.findIndex((t) => t.id === item.id)
	);

	return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid
        container
        wrap="nowrap"
        spacing={0}
        sx={{ position: "relative", flex: 1 }}
      >
        <Grid
          item
          xs
          lg
          md
          sm
          sx={{
            pl: 0,
            transition: "padding-left 0.3s ease",
          }}
        >
          <Box>
            <Divider sx={{ mt: 0 }} />
            <AppliedFilters />
            <Box px={2} sx={{ width: "100%", overflow: "hidden" }}>
              <KpiSummaries
                selectedKPI={selectedKPI ?? ""}
                setSelectedKPI={
                  setSelectedKPI ??
                  (() => {
                    console.log("setSelectedKPI");
                  })
                }
                KPICount={KPICount}
              />
            </Box>
          </Box>
          <Box ref={boxRef}>
            <SortedDataGrid7
              rows={uniqueData ? uniqueData : []}
              columns={filteredColumns}
              pagination
              disableColumnSelector
              disableDensitySelector
              disableColumnMenu
              disableRowSelectionOnClick
              disableColumnReorder
              unstable_rowSpanning={true}
              defaultSortField="workerName"
              loading={monthlyActivityReportStatus === "pending"}
              slots={{
                noRowsOverlay: NoReportsAvailable,
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              sx={{
                height: height,
                flex: 1,
                mt: 1,
                border: "none !important",
                "& .MuiDataGrid-columnHeader": {
                  "&.hover-column-header": {
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      display: "none",
                    },
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    display: "none",
                  },
                },
                ".MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                // "& .MuiDataGrid-row": {
                // 	cursor: "pointer",
                // },
                "& .MuiTablePagination-root": {
                  marginRight: "60px",
                },
                ".MuiDataGrid-cell": {
                  "&:focus": {
                    outline: "none",
                  },
                  "&.hover-column-cell": {
                    position: "sticky",
                    right: 0,
                    zIndex: 1,
                  },
                  "& .hover-content": {
                    transition: "opacity 0.3s ease-in-out",
                    opacity: 0,
                  },
                },
                ".MuiDataGrid-columnHeader:hover": {
                  "& .MuiDataGrid-columnSeparator": {
                    display: "flex",
                  },
                },
                ".MuiDataGrid-row:hover .hover-column-cell .hover-content": {
                  opacity: 1,
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row.Mui-selected .hover-column-cell .hover-content":
                  {
                    backgroundColor: "#e7e9f0",
                  },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MonthlyActivityTable;
